import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecipeApi } from "../api/RecipeApi";
import { useCategoryApi } from "../api/CategoryApi";
import { useUser } from "../context/UserContext";
import { useFontenehus } from "../context/FontenehusContext";
import { handleApiError } from "../utils/handleApiError";
import type { Recipe } from "../types/Recipe";
import type { Filters } from "../components/FilterModal";
import FilterModal from "../components/FilterModal";
import { useAllergenApi } from "../api/AllergenApi";
import { useIngredientApi } from "../api/IngredientApi";
import { createPortal } from "react-dom";
import RecipeDetail from "./RecipeDetailPage";
import { useRefresh } from "../context/RefreshContext ";
const RecipesPage: React.FC = () => {
  const navigate = useNavigate();
  const { fontenehusList } = useFontenehus();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [recipeCategories, setRecipeCategories] = useState<any[]>([]);
  const [allergensList, setAllergensList] = useState<
    { id: number; name: string }[]
  >([]);
  const [ingredientsList, setIngredientsList] = useState<
    { id: number; name: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    categoryIds: [],
    fontenehusIds: [],
    allergenIds: [],
    ingredientIds: [],
  });
  const { refreshKey } = useRefresh();
  const [showModalRecipe, setShowModalRecipe] = useState<number | undefined>(
    undefined,
  );

  const filteredRecipes = recipes.filter((recipe) => {
    const matchesSearch =
      recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (recipe.description?.toLowerCase().includes(searchTerm.toLowerCase()) ??
        false);

    const matchesCategory =
      filters.categoryIds.length === 0 ||
      (recipe.categories ?? []).some((rc) =>
        filters.categoryIds.includes(rc.id),
      );

    const matchesFontenehus =
      filters.fontenehusIds.length === 0 ||
      filters.fontenehusIds.includes(recipe.fontenehusId);

    const matchesAllergens =
      filters.allergenIds.length === 0 ||
      !(recipe.allergens ?? []).some((a) => filters.allergenIds.includes(a.id));

    const matchesIngredients =
      filters.ingredientIds.length === 0 ||
      (recipe.ingredients ?? []).some((ri) =>
        filters.ingredientIds.includes(ri.id),
      );

    return (
      matchesSearch &&
      matchesCategory &&
      matchesFontenehus &&
      matchesAllergens &&
      matchesIngredients
    );
  });

  const [currentPage, setCurrentPage] = useState(1);
  const recipesPerPage = 12; // Adjust as needed

  // Pagination calculations
  const totalPages = Math.ceil(filteredRecipes.length / recipesPerPage);
  const paginatedRecipes = filteredRecipes.slice(
    (currentPage - 1) * recipesPerPage,
    currentPage * recipesPerPage,
  );

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" }); // scroll to top
  };

  const { user } = useUser();
  const canAddRecipe = !user?.roles?.some((r) => ["Guest"].includes(r));

  const { getRecipes } = useRecipeApi();
  const { getCategories } = useCategoryApi();
  const { getAllergens } = useAllergenApi();
  const { getIngredients } = useIngredientApi();

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const recipesResp = await getRecipes();
        setRecipes(recipesResp.data.data);

        const categoriesResp = await getCategories();
        setRecipeCategories(categoriesResp.data.data);
        const [allergenResp, ingredientResp] = await Promise.all([
          getAllergens(),
          getIngredients(),
        ]);
        setIngredientsList(ingredientResp.data.data);
        setAllergensList(allergenResp.data.data);
      } catch (err) {
        handleApiError(err, "En feil oppsto ved lasting av oppskrifter.");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [refreshKey]);

  return (
    <div className="min-h-screen bg-orange-50 pt-24 pb-20 px-4 sm:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-orange-600 mb-2">
            Våre Oppskrifter
          </h1>
          <p className="text-gray-700 text-lg sm:text-xl">
            Bla gjennom alle oppskriftene våre eller søk etter en rett.
          </p>
        </div>

        {/* Add Recipe Button */}
        {canAddRecipe && (
          <div className="flex justify-center mb-8">
            <button
              onClick={() => navigate("/oppskrifter/ny")}
              className="bg-orange-500 text-white font-semibold px-6 py-3 rounded-xl shadow hover:bg-orange-600 transition transform hover:-translate-y-0.5"
            >
              ➕ Legg til ny oppskrift
            </button>
          </div>
        )}

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <input
            type="text"
            placeholder="🔍 Søk etter oppskrift..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // reset page on search
            }}
            className="w-full sm:w-2/3 border border-gray-300 rounded-lg px-4 py-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 transition"
          />
          <button
            onClick={() => setFiltersOpen(true)}
            className="w-full sm:w-1/3 bg-orange-500 text-white px-4 py-3 rounded-lg shadow hover:bg-orange-600 transition"
          >
            Filtrer
          </button>
        </div>
        {/* Pagination Controls (Top) */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-4 mt-10 mb-10 flex-wrap">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 transition"
            >
              {"<"} Forrige
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={`px-4 py-1 rounded transition ${
                  page === currentPage
                    ? "bg-orange-500 text-white"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 transition"
            >
              Neste {">"}
            </button>
          </div>
        )}

        {/* Recipe Modal Portal */}
        {showModalRecipe &&
          createPortal(
            <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
              <RecipeDetail
                isOpen={showModalRecipe != undefined}
                isModal
                recipeId={showModalRecipe}
                onClose={() => setShowModalRecipe(undefined)}
                isRecipeFull={true}
              />
            </div>,
            document.body,
          )}

        {/* Recipe Grid */}
        {loading ? (
          <p className="text-center text-gray-600 italic mt-10">
            Laster oppskrifter...
          </p>
        ) : filteredRecipes.length === 0 ? (
          <p className="text-center text-gray-600 italic mt-10">
            Ingen oppskrifter funnet.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedRecipes.map((recipe) => (
                <div
                  key={recipe.id}
                  onClick={() => setShowModalRecipe(recipe.id)}
                  className="cursor-pointer bg-white rounded-2xl shadow-md hover:shadow-xl transition transform hover:-translate-y-1 group overflow-hidden"
                >
                  <div className="w-full overflow-hidden min-h-[400px] flex items-center justify-center bg-gray-100">
                    <img
                      src={
                        recipe.imageUrl || "/assets/no-image-placeholder.webp"
                      }
                      alt={recipe.title}
                      className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4 sm:p-5">
                    <h2 className="text-xl font-semibold text-orange-600 mb-2">
                      {recipe.title}
                    </h2>
                    <p className="text-gray-700 text-sm sm:text-base">
                      {recipe.description || "Ingen beskrivelse."}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-8 flex-wrap">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                >
                  {"<"} Forrige
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      className={`px-3 py-1 rounded ${
                        page === currentPage
                          ? "bg-orange-500 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                >
                  Neste {">"}
                </button>
              </div>
            )}
          </>
        )}

        {/* Filter Modal */}
        <FilterModal
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          categories={recipeCategories}
          fontenehusList={fontenehusList}
          allergens={allergensList}
          ingredients={ingredientsList}
          onApplyFilters={setFilters}
        />
      </div>
    </div>
  );
};
export default RecipesPage;
